import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Widget, Box, Typography, ProgressList, IconTextButton } from '@scope/scope-one-ui/core';
import Download from 'assets/icons/download.svg';
import { COLORS } from '@scope/scope-one-ui/theme';
import { useGetContributionByCountry, useGetExportUrl } from '../../services/climateStressTest';
import { ContributionByCountry, PageData } from '../../types/climateStressTest';
import { Tooltip } from './PhysicalRisk.styled';

export const CountryContribution: FC<PageData> = ({ entity }) => {
  const { t } = useTranslation();

  const { data: orderly } = useGetContributionByCountry(entity.country.code, 'orderly');
  const { data: disorderly } = useGetContributionByCountry(entity.country.code, 'disorderly');
  const { data: hotHouse } = useGetContributionByCountry(entity.country.code, 'hotHouse');
  const exportFile = useGetExportUrl(
    'contribution-to-temperature-change-by-country',
    entity.country.code,
    entity.sector.code,
  );

  /* Sets maximum scale to 125% of highest available value (abs) to make data nicely visible */
  const max =
    !orderly || !disorderly || !hotHouse
      ? 0
      : Math.max(
          Math.abs(orderly.value),
          Math.abs(orderly.worldAverage),
          Math.abs(orderly.worldBest),
          Math.abs(orderly.worldWorst),
          Math.abs(disorderly.value),
          Math.abs(disorderly.worldAverage),
          Math.abs(disorderly.worldBest),
          Math.abs(disorderly.worldWorst),
          Math.abs(hotHouse.value),
          Math.abs(hotHouse.worldAverage),
          Math.abs(hotHouse.worldBest),
          Math.abs(hotHouse.worldWorst),
        ) * 1.25;

  const getCountData = (count?: ContributionByCountry) => {
    if (!count) return [];

    return [
      { label: entity.country.name, count: +count.value.toFixed(1), color: COLORS.primary.main },
      { label: 'World average', count: +count.worldAverage.toFixed(1), color: COLORS.warning.main },
      { label: `World's best`, count: +count.worldBest.toFixed(1), color: COLORS.success.main },
      { label: `World's worst`, count: +count.worldWorst.toFixed(1), color: COLORS.error.main },
    ];
  };

  const data = [
    { label: t('Orderly'), count: getCountData(orderly) },
    { label: t('Disorderly'), count: getCountData(disorderly) },
    { label: t('Hot house'), count: getCountData(hotHouse) },
  ];

  return (
    <Box display="flex" flexDirection="column" gridGap={2}>
      <Widget
        title={t('Country contribution to temperature change')}
        customHeader={
          <IconTextButton icon={<Download />} onClick={exportFile}>
            Export
          </IconTextButton>
        }
        tooltip={
          <Tooltip display="flex" flexDirection="column">
            <Typography variant="body1" color={COLORS.typography.label}>
              {t('Relative contribution of the country to the average temperature change')}
            </Typography>
          </Tooltip>
        }
      />
      <Box display="flex" gridGap={4}>
        {data.map(({ label, count }) => (
          <Box key={label} display="flex" flexDirection="column" flex={1}>
            <Widget title={label}>
              <ProgressList data={count} percentage total={max} min={-max} />
            </Widget>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
